import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getCliente, updateCliente, getPedidos, getMensagens, getNotificacoes, marcarMensagemLida, marcarNotificacaoLida, solicitarCancelamentoPedido } from '../services/api';
import { getImageUrl } from '../utils/imageUrl';
import { toast } from 'react-toastify';
import './MinhaConta.css';

const ABA_DADOS = 'dados';
const ABA_PEDIDOS = 'pedidos';
const ABA_MENSAGENS = 'mensagens';
const ABA_NOTIFICACOES = 'notificacoes';

const MinhaConta = () => {
  const navigate = useNavigate();
  const clienteStorage = JSON.parse(localStorage.getItem('cliente') || 'null');
  const [abaAtiva, setAbaAtiva] = useState(ABA_DADOS);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    endereco: '',
    cidade: '',
    estado: '',
    cep: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [mensagens, setMensagens] = useState([]);
  const [notificacoes, setNotificacoes] = useState([]);

  const loadCliente = useCallback(async () => {
    try {
      const { data } = await getCliente(clienteStorage.id);
      setFormData({
        nome: data.nome || '',
        email: data.email || '',
        telefone: data.telefone || '',
        endereco: data.endereco || '',
        cidade: data.cidade || '',
        estado: data.estado || '',
        cep: data.cep || '',
      });
    } catch (err) {
      toast.error('Erro ao carregar dados. Faça login novamente.');
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [clienteStorage?.id, navigate]);

  useEffect(() => {
    if (!clienteStorage?.id) {
      navigate('/login');
      return;
    }
    loadCliente();
  }, [clienteStorage?.id, navigate, loadCliente]);

  useEffect(() => {
    if (!clienteStorage?.id) return;
    if (abaAtiva === ABA_PEDIDOS) loadPedidos();
    if (abaAtiva === ABA_MENSAGENS) loadMensagens();
    if (abaAtiva === ABA_NOTIFICACOES) loadNotificacoes();
  }, [abaAtiva, clienteStorage?.id]);

  const loadPedidos = async () => {
    try {
      const { data } = await getPedidos();
      setPedidos(Array.isArray(data) ? data : []);
    } catch {
      setPedidos([]);
    }
  };

  const loadMensagens = async () => {
    try {
      const { data } = await getMensagens();
      setMensagens(Array.isArray(data) ? data : []);
    } catch {
      setMensagens([]);
    }
  };

  const loadNotificacoes = async () => {
    try {
      const { data } = await getNotificacoes();
      setNotificacoes(Array.isArray(data) ? data : []);
    } catch {
      setNotificacoes([]);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateCliente(clienteStorage.id, formData);
      const updated = { ...clienteStorage, nome: formData.nome };
      localStorage.setItem('cliente', JSON.stringify(updated));
      toast.success('Dados atualizados com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao atualizar dados.');
    } finally {
      setSaving(false);
    }
  };

  const handleMarcarMensagemLida = async (id) => {
    try {
      await marcarMensagemLida(id);
      setMensagens((prev) => prev.map((m) => (m.id === id ? { ...m, lida: 1 } : m)));
    } catch {
      toast.error('Erro ao marcar mensagem.');
    }
  };

  const handleMarcarNotificacaoLida = async (id) => {
    try {
      await marcarNotificacaoLida(id);
      setNotificacoes((prev) => prev.map((n) => (n.id === id ? { ...n, lida: 1 } : n)));
    } catch {
      toast.error('Erro ao marcar notificação.');
    }
  };

  const handleSolicitarCancelamento = async (pedidoId) => {
    try {
      await solicitarCancelamentoPedido(pedidoId);
      toast.success('Solicitação de cancelamento enviada para o administrador.');
      loadPedidos();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao solicitar cancelamento.');
    }
  };

  const formatDate = (str) => {
    if (!str) return '-';
    const d = new Date(str);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const statusLabel = {
    pendente: 'Pendente',
    confirmado: 'Confirmado',
    enviado: 'Enviado',
    entregue: 'Entregue',
    cancelado: 'Cancelado',
    aguardando_aprovacao: 'Aguardando aprovação',
    cancelamento_solicitado: 'Cancelamento solicitado',
  };

  const formaPagamentoLabel = {
    pix: 'Pix',
    dinheiro: 'Dinheiro',
    debito: 'Débito',
    credito: 'Crédito',
    prazo: 'Prazo',
  };

  if (loading) {
    return (
      <div className="minha-conta-page dashboard-cliente">
        <div className="container">
          <p className="loading">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="minha-conta-page dashboard-cliente">
      <Helmet>
        <title>Minha Conta | Loucas por Beleza</title>
        <meta name="description" content="Acompanhe seus pedidos, mensagens e notificações de promoção." />
      </Helmet>
      <div className="container">
        <h1 className="page-title">Minha Conta</h1>
        <p className="dashboard-subtitle">Olá, {formData.nome || clienteStorage?.nome}. Acompanhe suas compras, mensagens e promoções.</p>

        <nav className="dashboard-tabs">
          <button type="button" className={abaAtiva === ABA_DADOS ? 'active' : ''} onClick={() => setAbaAtiva(ABA_DADOS)}>Meus dados</button>
          <button type="button" className={abaAtiva === ABA_PEDIDOS ? 'active' : ''} onClick={() => setAbaAtiva(ABA_PEDIDOS)}>Meus pedidos</button>
          <button type="button" className={abaAtiva === ABA_MENSAGENS ? 'active' : ''} onClick={() => setAbaAtiva(ABA_MENSAGENS)}>Mensagens</button>
          <button type="button" className={abaAtiva === ABA_NOTIFICACOES ? 'active' : ''} onClick={() => setAbaAtiva(ABA_NOTIFICACOES)}>Notificações de promoção</button>
        </nav>

        <div className="dashboard-content">
          {abaAtiva === ABA_DADOS && (
            <form onSubmit={handleSubmit} className="minha-conta-form">
              <div className="form-group">
                <label htmlFor="nome">Nome</label>
                <input type="text" id="nome" name="nome" value={formData.nome} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="email">E-mail</label>
                <input type="email" id="email" name="email" value={formData.email} readOnly disabled className="input-readonly" />
                <span className="form-hint">O e-mail não pode ser alterado.</span>
              </div>
              <div className="form-group">
                <label htmlFor="telefone">Telefone</label>
                <input type="text" id="telefone" name="telefone" value={formData.telefone} onChange={handleChange} placeholder="(11) 99999-9999" />
              </div>
              <div className="form-group">
                <label htmlFor="cep">CEP</label>
                <input type="text" id="cep" name="cep" value={formData.cep} onChange={handleChange} placeholder="00000-000" />
              </div>
              <div className="form-group">
                <label htmlFor="endereco">Endereço</label>
                <input type="text" id="endereco" name="endereco" value={formData.endereco} onChange={handleChange} placeholder="Rua, número, complemento" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="cidade">Cidade</label>
                  <input type="text" id="cidade" name="cidade" value={formData.cidade} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="estado">Estado</label>
                  <input type="text" id="estado" name="estado" value={formData.estado} onChange={handleChange} placeholder="UF" maxLength={2} />
                </div>
              </div>
              <button type="submit" className="btn-submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar alterações'}</button>
            </form>
          )}

          {abaAtiva === ABA_PEDIDOS && (
            <section className="dashboard-section">
              {pedidos.length === 0 ? (
                <div className="empty-state">
                  <p>Você ainda não tem pedidos.</p>
                  <Link to="/produtos" className="btn-secondary">Ver produtos</Link>
                </div>
              ) : (
                <ul className="pedidos-lista">
                  {pedidos.map((pedido) => (
                    <li key={pedido.id} className="pedido-card">
                      <div className="pedido-header">
                        <span className="pedido-num">Pedido #{pedido.id}</span>
                        <span className="pedido-data">{formatDate(pedido.created_at)}</span>
                        <span className={`pedido-status status-${pedido.status}`}>{statusLabel[pedido.status] || pedido.status}</span>
                      </div>
                      <div className="pedido-total">Total: R$ {parseFloat(pedido.total).toFixed(2)}</div>
                      <div className="pedido-pagamento">
                        <strong>Forma de pagamento:</strong> {formaPagamentoLabel[pedido.forma_pagamento] || pedido.forma_pagamento || '-'}
                        {pedido.forma_pagamento === 'prazo' && (
                          <>
                            {pedido.valor_entrada > 0 && (
                              <span> — Entrada: R$ {parseFloat(pedido.valor_entrada).toFixed(2)} ({formaPagamentoLabel[pedido.forma_entrada] || pedido.forma_entrada})</span>
                            )}
                            {pedido.parcelas >= 1 && (
                              <span> — {pedido.parcelas}x de R$ {parseFloat(pedido.valor_parcela || 0).toFixed(2)}</span>
                            )}
                            {pedido.total_com_juros != null && (
                              <span> (total c/ juros: R$ {parseFloat(pedido.total_com_juros).toFixed(2)})</span>
                            )}
                            {pedido.parcelas_datas && Array.isArray(pedido.parcelas_datas) && pedido.parcelas_datas.length > 0 && (
                              <ul className="pedido-parcelas-datas">
                                {pedido.parcelas_datas.map((data, i) => (
                                  <li key={i}>Parcela {i + 1}: venc. {new Date(data + 'T12:00:00').toLocaleDateString('pt-BR')}</li>
                                ))}
                              </ul>
                            )}
                          </>
                        )}
                      </div>
                      {pedido.itens && pedido.itens.length > 0 && (
                        <ul className="pedido-itens">
                          {pedido.itens.map((item) => {
                            const foto = (item.fotos && item.fotos[0]) || item.foto;
                            return (
                              <li key={item.id} className="pedido-item">
                                {foto && <img src={getImageUrl(foto)} alt={item.nome_produto} />}
                                <div>
                                  <strong>{item.nome_produto}</strong>
                                  {item.cor_selecionada && (
                                    <span className="pedido-item-cor">Cor: {item.cor_selecionada}</span>
                                  )}
                                  <span>{item.quantidade} × R$ {parseFloat(item.preco_unitario).toFixed(2)}</span>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {(() => {
                        const status = String(pedido.status || '').toLowerCase();
                        const podeSolicitar = ['pendente', 'confirmado', 'aguardando_aprovacao'].includes(status);
                        return podeSolicitar ? (
                          <div className="pedido-actions">
                            <button
                              type="button"
                              className="btn-solicitar-cancelamento"
                              onClick={() => handleSolicitarCancelamento(pedido.id)}
                            >
                              Solicitar cancelamento
                            </button>
                          </div>
                        ) : null;
                      })()}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {abaAtiva === ABA_MENSAGENS && (
            <section className="dashboard-section">
              {mensagens.length === 0 ? (
                <div className="empty-state">
                  <p>Nenhuma mensagem recebida.</p>
                </div>
              ) : (
                <ul className="mensagens-lista">
                  {mensagens.map((m) => (
                    <li key={m.id} className={`mensagem-card ${m.lida ? 'lida' : ''}`}>
                      <div className="mensagem-header">
                        <strong>{m.titulo}</strong>
                        <span className="mensagem-data">{formatDate(m.created_at)}</span>
                        {!m.lida && (
                          <button type="button" className="btn-marcar-lida" onClick={() => handleMarcarMensagemLida(m.id)}>Marcar como lida</button>
                        )}
                      </div>
                      <p className="mensagem-texto">{m.mensagem}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {abaAtiva === ABA_NOTIFICACOES && (
            <section className="dashboard-section">
              {notificacoes.length === 0 ? (
                <div className="empty-state">
                  <p>Nenhuma notificação de promoção no momento.</p>
                </div>
              ) : (
                <ul className="notificacoes-lista">
                  {notificacoes.map((n) => (
                    <li key={n.id} className={`notificacao-card ${n.lida ? 'lida' : ''}`}>
                      <div className="notificacao-header">
                        <strong>{n.titulo}</strong>
                        <span className="notificacao-data">{formatDate(n.created_at)}</span>
                        {!n.lida && (
                          <button type="button" className="btn-marcar-lida" onClick={() => handleMarcarNotificacaoLida(n.id)}>Marcar como lida</button>
                        )}
                      </div>
                      {n.texto && <p className="notificacao-texto">{n.texto}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>

        <p className="minha-conta-footer">
          <Link to="/">Voltar à loja</Link>
        </p>
      </div>
    </div>
  );
};

export default MinhaConta;
